import React from "react";

const HTMLSemantics = () => {
  return (
    <div className="html-container">
      <h1>HTML – Semantics</h1>

      {/* 1. Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          Semantic HTML provides meaning to the web page structure by using elements that describe their purpose. This enhances accessibility, SEO, and maintainability.
        </p>
      </section>

      {/* 2. Structural Semantic Elements */}
      <section id="structural-elements">
        <h2>2. Structural Semantic Elements</h2>
        <p>These elements define the layout and structure of a web page:</p>
        <ul>
          <li>
            <code>&lt;header&gt;</code>: Represents the introductory content or navigation.
          </li>
          <li>
            <code>&lt;nav&gt;</code>: Defines a section for navigation links.
          </li>
          <li>
            <code>&lt;main&gt;</code>: Represents the main content of the page.
          </li>
          <li>
            <code>&lt;section&gt;</code>: Groups related content together.
          </li>
          <li>
            <code>&lt;article&gt;</code>: Represents independent, self-contained content.
          </li>
          <li>
            <code>&lt;aside&gt;</code>: Represents content tangentially related to the main content.
          </li>
          <li>
            <code>&lt;footer&gt;</code>: Represents footer content for a page or section.
          </li>
        </ul>
      </section>

      {/* 3. Text-Level Semantic Elements */}
      <section id="text-elements">
        <h2>3. Text-Level Semantic Elements</h2>
        <p>These elements give meaning to text:</p>
        <ul>
          <li>
            <code>&lt;strong&gt;</code>: Denotes important text.
          </li>
          <li>
            <code>&lt;em&gt;</code>: Denotes emphasis.
          </li>
          <li>
            <code>&lt;mark&gt;</code>: Highlights text.
          </li>
          <li>
            <code>&lt;abbr&gt;</code>: Represents abbreviations or acronyms.
          </li>
          <li>
            <code>&lt;cite&gt;</code>: Represents a reference to a creative work.
          </li>
          <li>
            <code>&lt;time&gt;</code>: Represents a date or time.
          </li>
          <li>
            <code>&lt;dfn&gt;</code>: Represents the defining instance of a term.
          </li>
        </ul>
      </section>

      {/* 4. Multimedia Semantic Elements */}
      <section id="multimedia-elements">
        <h2>4. Multimedia Semantic Elements</h2>
        <ul>
          <li>
            <code>&lt;figure&gt;</code>: Represents self-contained content like images or diagrams.
          </li>
          <li>
            <code>&lt;figcaption&gt;</code>: Provides a caption for <code>&lt;figure&gt;</code>.
          </li>
          <li>
            <code>&lt;audio&gt;</code> and <code>&lt;video&gt;</code>: For media content.
          </li>
        </ul>
      </section>

      {/* 5. Forms and Interactive Elements */}
      <section id="forms-elements">
        <h2>5. Forms and Interactive Elements</h2>
        <ul>
          <li>
            <code>&lt;form&gt;</code>: Represents a form for user input.
          </li>
          <li>
            <code>&lt;fieldset&gt;</code> and <code>&lt;legend&gt;</code>: Groups form controls with a label.
          </li>
          <li>
            <code>&lt;label&gt;</code>: Associates text with a form control.
          </li>
          <li>
            <code>&lt;button&gt;</code>: Represents an actionable button.
          </li>
        </ul>
      </section>

      {/* 6. Benefits of Semantic HTML */}
      <section id="benefits">
        <h2>6. Benefits of Semantic HTML</h2>
        <ul>
          <li>Improves accessibility for screen readers and assistive technologies.</li>
          <li>Enhances SEO by providing meaningful page structure.</li>
          <li>Improves code maintainability and readability.</li>
          <li>Provides better default styling in browsers.</li>
          <li>Supports modern web standards and tools.</li>
        </ul>
      </section>

      {/* 7. Best Practices */}
      <section id="best-practices">
        <h2>7. Best Practices</h2>
        <ul>
          <li>Use semantic elements instead of generic <code>&lt;div&gt;</code> or <code>&lt;span&gt;</code> when possible.</li>
          <li>Ensure correct nesting of elements for clarity.</li>
          <li>Combine semantic HTML with CSS for presentation, not structure.</li>
          <li>Use ARIA roles only when semantic elements cannot provide sufficient meaning.</li>
        </ul>
      </section>

      {/* 8. Summary */}
      <section id="summary">
        <h2>8. Summary</h2>
        <p>
          Semantic HTML is the foundation of modern web development. It improves accessibility, SEO, and maintainability while providing meaningful structure for both humans and machines.
        </p>
      </section>
    </div>
  );
};

export default HTMLSemantics;
